import client from './client';

/**
 * Store credits — the admin side.
 *
 * Credits are GRANTED, never deposited: there is no path anywhere in this API
 * for a customer to put their own money in, and no path here to take a balance
 * away. The only write is add-credits, and it is irreversible, which is why the
 * page in front of it insists on a preview and a confirmation.
 */

export interface WalletSummary {
  total_credits_outstanding: number;
  total_credits_issued_lifetime: number;
  total_credits_redeemed_lifetime: number;
  total_credits_expired_lifetime: number;
  users_with_balance: number;
  avg_balance_per_user: number;
}

export async function getWalletSummary(): Promise<WalletSummary> {
  const res = await client.get('/api/admin/wallet/summary');
  return res.data;
}

export interface WalletHolder {
  user_id: string;
  display_name: string | null;
  email: string | null;
  balance: number;
  expires_soonest_at: string | null;
  last_transaction_at: string | null;
}

export interface HolderList {
  users: WalletHolder[];
  total: number;
  totalPages: number;
  /** Server's definition of "expiring soon", so this app doesn't invent one. */
  expiringSoonDays: number;
}

export async function getWalletHolders(params: {
  page?: number;
  limit?: number;
  min_balance?: number;
  has_expiry_soon?: boolean;
} = {}): Promise<HolderList> {
  const limit = params.limit ?? 20;
  const res = await client.get('/api/admin/wallet/users', {
    params: {
      page: params.page ?? 1,
      limit,
      ...(params.min_balance ? { min_balance: params.min_balance } : {}),
      ...(params.has_expiry_soon ? { has_expiry_soon: true } : {}),
    },
  });
  const data = res.data ?? {};
  const pagination = data.pagination ?? {};
  return {
    users: Array.isArray(data.users) ? data.users : [],
    total: pagination.total ?? 0,
    totalPages: pagination.pages ?? 1,
    expiringSoonDays: data.expiring_soon_days ?? 7,
  };
}

export interface WalletTransaction {
  id: string;
  type: 'credit_added' | 'credit_used' | 'credit_expired' | 'credit_refunded';
  amount: number;
  reference: string | null;
  order_id: string | null;
  expires_at: string | null;
  created_at: string;
  /** Admin who issued the grant. Present only on this admin-side view. */
  created_by: string | null;
}

export interface WalletUserDetail {
  user: { user_id: string; display_name: string | null; email: string | null };
  balance: number;
  lifetime_earned: number;
  lifetime_used: number;
  lifetime_expired: number;
  expires_soonest_at: string | null;
  transactions: WalletTransaction[];
}

export async function getWalletUser(userId: string): Promise<WalletUserDetail> {
  const res = await client.get(`/api/admin/wallet/user/${encodeURIComponent(userId)}`);
  return res.data;
}

export interface FilterPreview {
  user_count: number;
  users_with_phone: number;
  users_without_phone: number;
}

export interface AudienceFilter {
  days?: number;
  has_ordered?: boolean;
  min_orders?: number;
}

/**
 * How many customers a filter selects, before anything is committed. The whole
 * point of the two-step flow: "credit 4,000 people" should never be discovered
 * after the money has gone out.
 */
export async function previewFilter(filter: AudienceFilter): Promise<FilterPreview> {
  const res = await client.get('/api/admin/wallet/preview-filter', {
    params: {
      ...(filter.days !== undefined ? { days: filter.days } : {}),
      ...(filter.has_ordered ? { has_ordered: true } : {}),
      ...(filter.min_orders !== undefined ? { min_orders: filter.min_orders } : {}),
    },
  });
  return res.data;
}

export interface AddCreditsRequest {
  /** Explicit list, OR filter — never both; the server rejects the pair. */
  user_ids?: string[];
  filter?: AudienceFilter;
  amount: number;
  reference: string;
  expires_at: string | null;
  send_notification: boolean;
  notification_template: string | null;
}

export interface AddCreditsResult {
  users_credited: number;
  total_credits_issued: number;
  /** Queued for delivery — WhatsApp's own outcome lands in the WA Messages log. */
  notifications_sent: number;
  users_without_phone: number;
}

export async function addCredits(body: AddCreditsRequest): Promise<AddCreditsResult> {
  const res = await client.post('/api/admin/wallet/add-credits', body);
  return res.data;
}

// ── WhatsApp template preview ────────────────────────────────────────────────

/**
 * The three parameters a store-credit template is sent with. Mirrors
 * creditTemplateParams() in the ecommerce service's lib/walletAudience — this
 * copy renders the admin's preview, that one builds the real message. Keep them
 * in step, or the preview stops being a preview.
 *
 * The amount is BARE — no ₹. The approved bodies print the symbol themselves
 * ("We've added ₹{{2}} in store credits"), so prefixing it here renders "₹₹200".
 */
export function creditTemplateParams(args: {
  displayName: string | null;
  amount: number;
  expiresAt: string | null;
}): string[] {
  return [
    (args.displayName || '').trim() || 'Valued Customer',
    args.amount.toLocaleString('en-IN'),
    formatExpiryForMessage(args.expiresAt),
  ];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatExpiryForMessage(expiresAt: string | null): string {
  if (!expiresAt) return 'no expiry date';
  const d = new Date(expiresAt);
  if (isNaN(d.getTime())) return 'no expiry date';
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Substitute {{1}}, {{2}}, … into a template body so the admin sees the message.
 *
 * If the body already prints a ₹ immediately before a placeholder and the value
 * carries one too, the value's is dropped — the preview must never show "₹₹200"
 * for a message Meta will render correctly.
 */
export function renderTemplateBody(body: string, params: string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (match, index, offset: number) => {
    const value = params[Number(index) - 1] ?? match;
    const charBefore = offset > 0 ? body.charAt(offset - 1) : '';
    if (charBefore === '₹' && value.charAt(0) === '₹') return value.slice(1);
    return value;
  });
}
